import React, { useEffect, useRef, useMemo, useState } from "react";
import CommonFooter from "../../core/common/footer/commonFooter";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { all_routes } from "../../Router/all_routes";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { getInvoiceDetail } from "../../api/invoiceApi";
import "./invoicedetail.css";

const Invoicedetails = () => {
  const route = all_routes;
  const { id } = useParams();
  const t = (key) => (window?.translate ? window.translate(key) : key);
  const currentLang = localStorage.getItem("lang") || "en";

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  const formatDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleDateString();
  };

  const fetchInvoiceDetail = async () => {
    try {
      setLoading(true);
      const response = await getInvoiceDetail(id);
      setInvoice(response?.data || null);
    } catch (error) {
      toast.error(error?.message || t("Failed to fetch invoice detail"));
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchInvoiceDetail();
  }, [id]);

  const printRef = useRef(null);

  const handlePrintInvoice = useReactToPrint({
    contentRef: printRef,
    documentTitle: `invoice-${invoice?.invoiceNo || "invoice"}`,
  });

  const customerName = invoice?.customer?.name || t("Walk-in Customer");
  const customerAddress = invoice?.customer?.address || t("N/A");
  const customerEmail = invoice?.customer?.email || t("N/A");
  const customerPhone = invoice?.customer?.phone || t("N/A");

  const storeName = invoice?.store?.storeName || t("Store");
  const storeEmail = invoice?.store?.email || t("N/A");
  const storePhone = invoice?.store?.phone || t("N/A");
  const storeCountry = invoice?.store?.country || t("N/A");

  const paymentStatusBadge = useMemo(() => {
    const status = invoice?.status || "Unpaid";

    if (status === "Paid") {
      return (
        <span className="bg-success text-white fs-10 px-1 rounded">
          <i className="ti ti-point-filled " />
          {t("Paid")}
        </span>
      );
    }

    if (status === "Overdue") {
      return (
        <span className="bg-warning text-white fs-10 px-1 rounded">
          <i className="ti ti-point-filled " />
          {t("Overdue")}
        </span>
      );
    }

    return (
      <span className="bg-danger text-white fs-10 px-1 rounded">
        <i className="ti ti-point-filled " />
        {t("Unpaid")}
      </span>
    );
  }, [invoice, currentLang]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">{t("Loading")}...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("Invoice Details")}</h4>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={t("Pdf")}
                  onClick={handlePrintInvoice}
                >
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={t("Print")}
                  onClick={handlePrintInvoice}
                >
                  <i className="ti ti-printer" />
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title={t("Collapse")}
                  id="collapse-header"
                >
                  <i className="ti ti-chevron-up" />
                </Link>
              </li>
            </ul>
            <div className="page-btn">
              <Link to={route.invoice} className="btn btn-primary">
                <i className="ti ti-arrow-left me-2" />
                {t("Back to Invoices")}
              </Link>
            </div>
          </div>

          <div className="card" ref={printRef}>
            <div className="card-body invoice-print-area">
              <div className="row justify-content-between align-items-center border-bottom mb-3">
                <div className="col-md-6">
                  <div className="mb-2">
                    <ImageWithBasePath
                      src="assets/img/logo.svg"
                      className="invoice-logo"
                      alt="logo"
                    />
                  </div>
                  <p>{storeCountry}</p>
                </div>
                <div className="col-md-6">
                  <div className=" text-end mb-3">
                    <h5 className="text-gray mb-1">
                      {t("Invoice No")}{" "}
                      <span className="text-primary">
                        #{invoice?.invoiceNo || t("N/A")}
                      </span>
                    </h5>
                    <p className="mb-1 fw-medium">
                      {t("Created Date")} :{" "}
                      <span className="text-dark">
                        {formatDate(invoice?.createdAt)}
                      </span>
                    </p>
                    <p className="fw-medium">
                      {t("Due Date")} :{" "}
                      <span className="text-dark">
                        {formatDate(invoice?.dueDate)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="row border-bottom mb-3">
                <div className="col-md-5">
                  <p className="text-dark mb-2 fw-semibold">{t("From")}</p>
                  <div>
                    <h4 className="mb-1">{storeName}</h4>
                    <p className="mb-1">{storeCountry}</p>
                    <p className="mb-1">
                      {t("Email")} : <span className="text-dark">{storeEmail}</span>
                    </p>
                    <p>
                      {t("Phone")} : <span className="text-dark">{storePhone}</span>
                    </p>
                  </div>
                </div>

                <div className="col-md-5">
                  <p className="text-dark mb-2 fw-semibold">{t("To")}</p>
                  <div>
                    <h4 className="mb-1">{customerName}</h4>
                    <p className="mb-1">{customerAddress}</p>
                    <p className="mb-1">
                      {t("Email")} : <span className="text-dark">{customerEmail}</span>
                    </p>
                    <p>
                      {t("Phone")} : <span className="text-dark">{customerPhone}</span>
                    </p>
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="mb-3">
                    <p className="text-title mb-2 fw-medium">{t("Payment Status")}</p>
                    {paymentStatusBadge}
                    <div className="mt-3">
                      <ImageWithBasePath
                        src="assets/img/qr.svg"
                        className="img-fluid"
                        alt="QR"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <p className="fw-medium">
                  {t("Invoice For")} :{" "}
                  <span className="text-dark fw-medium">{t("POS Sale Invoice")}</span>
                </p>

                <div className="table-responsive mb-3">
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th>{t("Job Description")}</th>
                        <th className="text-end">{t("Qty")}</th>
                        <th className="text-end">{t("Cost")}</th>
                        <th className="text-end">{t("Discount")}</th>
                        <th className="text-end">{t("Total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice?.items || []).length > 0 ? (
                        invoice.items.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <h6>{item?.name || t("Item")}</h6>
                            </td>
                            <td className="text-gray-9 fw-medium text-end">
                              {Number(item?.quantity || 0)}
                            </td>
                            <td className="text-gray-9 fw-medium text-end">
                              {formatMoney(item?.price || 0)}
                            </td>
                            <td className="text-gray-9 fw-medium text-end">
                              $0.00
                            </td>
                            <td className="text-gray-9 fw-medium text-end">
                              {formatMoney(item?.total || 0)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            {t("No items found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row border-bottom mb-3">
                <div className="col-md-5 ms-auto mb-3">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3">
                    <p className="mb-0">{t("Sub Total")}</p>
                    <p className="text-dark fw-medium mb-2">
                      {formatMoney(invoice?.amount || 0)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2 pe-3">
                    <p className="mb-0">{t("Discount(0%)")}</p>
                    <p className="text-dark fw-medium mb-2">$0.00</p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                    <p className="mb-0">{t("VAT(0%)")}</p>
                    <p className="text-dark fw-medium mb-2">$0.00</p>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pe-3">
                    <h5>{t("Total Amount")}</h5>
                    <h5>{formatMoney(invoice?.amount || 0)}</h5>
                  </div>
                  <p className="fs-12">
                    {t("Amount in Words")} : {formatMoney(invoice?.amount || 0)}
                  </p>
                </div>
              </div>

              <div className="row align-items-center border-bottom mb-3">
                <div className="col-md-7">
                  <div>
                    <div className="mb-3">
                      <h6 className="mb-1">{t("Terms and Conditions")}</h6>
                      <p>{t("Please keep this invoice for your records.")}</p>
                    </div>
                    <div className="mb-3">
                      <h6 className="mb-1">{t("Notes")}</h6>
                      <p>
                        {invoice?.paymentNote ||
                          invoice?.saleNote ||
                          t("No notes available.")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-5">
                  <div className="text-end">
                    <ImageWithBasePath
                      src="assets/img/sign.svg"
                      className="img-fluid"
                      alt="sign"
                    />
                  </div>
                  <div className="text-end mb-3">
                    <h6 className="fs-14 fw-medium pe-3">
                      {invoice?.user?.name || t("N/A")}
                    </h6>
                    <p>{invoice?.user?.email || t("Handled By")}</p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="mb-3">
                  <ImageWithBasePath
                    src="assets/img/logo.svg"
                    className="invoice-logo"
                    alt="logo"
                  />
                </div>
                <p className="text-dark mb-1">
                  {t("Payment Made Via")} {invoice?.paymentType || t("N/A")}
                </p>
                <div className="d-flex justify-content-center align-items-center flex-wrap">
                  <p className="fs-12 mb-0 me-3">
                    {t("Paid")} :{" "}
                    <span className="text-dark">
                      {formatMoney(invoice?.paid || 0)}
                    </span>
                  </p>
                  <p className="fs-12 mb-0 me-3">
                    {t("Due")} :{" "}
                    <span className="text-dark">
                      {formatMoney(invoice?.amountDue || 0)}
                    </span>
                  </p>
                  <p className="fs-12">
                    {t("Receiver")} :{" "}
                    <span className="text-dark">
                      {invoice?.paymentReceiver || t("N/A")}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center align-items-center mb-4">
            <Link
              to="#"
              className="btn btn-primary d-flex justify-content-center align-items-center me-2 ms-2"
              onClick={handlePrintInvoice}
            >
              <i className="ti ti-printer me-2" />
              {t("Print Invoice")}
            </Link>
            <Link
              to={route.invoice}
              className="btn btn-secondary d-flex justify-content-center align-items-center border"
            >
              <i className="ti ti-copy me-2" />
              {t("Back")}
            </Link>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default Invoicedetails;
